import { RayCaster } from "../controls";
import { setPointScale } from '../utilities';
import earcut from 'earcut';
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Face3,
  Geometry,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  Scene,
  SphereGeometry,
  Triangle,
  Vector3
} from "three";
import {
  pointInPolygon,
  checkValidPolygon,
  planeFromVectors
} from "../utilities/polygon";
import { textToColor } from "./categories";
import { CameraImage } from "../cameras/cameras";
import { ImageLabelTools } from ".";
import { ImageLabel, LabelCategory } from "./image-label";

class LabelPoint extends Mesh {
  public coordinate: Coordinate;

  constructor(geometry, material, coordinate) {
    super(geometry, material);
    this.coordinate = coordinate;
  }
}

class Coordinate {
  public pixel: {u: number, v: number};
  public direction: Vector3
  public position: Vector3;

  constructor(pixel, direction, position) {
    this.updateValues(pixel, direction, position);
  }

  updateValues(pixel, direction, position) {
    this.pixel = pixel;
    this.direction = direction;
    this.position = position;
  }
}

class SceneLabel extends ImageLabel {
  public camera: CameraImage;
  public coordinates: Coordinate[] = [];
  public minCoordinates = 3;
  private standardMeshes = [];
  private opacityMeshes = [];
  private pointGeometry: SphereGeometry;
  private pointMaterial: MeshBasicMaterial;
  private lineGeometry: BufferGeometry;
  private lineMaterial: LineBasicMaterial;
  private polygonMaterial: MeshBasicMaterial;

  constructor(category, camera: CameraImage) {
    super(category);
    this.camera = camera;
    this.pointGeometry = new SphereGeometry(0.05, 20, 20);
    this.lineGeometry = new BufferGeometry();
    this.updateMaterials();
  }

  get hasEnoughPoints() {
    return this.numPoints >= this.minCoordinates;
  }

  get numPoints() {
    return this.coordinates.length;
  }

  get positions() {
    return this.coordinates.map(x => x.position);
  }

  get directions() {
    return this.coordinates.map(x => x.direction);
  }

  get pixels() {
    return this.coordinates.map(x => x.pixel);
  }

  get viewer() {
    return this.category.sceneLabels.viewer;
  }

  get standardScene() {
    return this.category.sceneLabels.standardScene;
  }

  get opacityScene() {
    return this.category.sceneLabels.opacityScene;
  }

  get standardColor() {
    return this.category.color;
  }

  get darkenedColor() {
    let dark = new Color()
      .copy(this.category.color)
      .multiplyScalar(0.75);

    return dark;
  }

  updateMaterials() {
    this.pointMaterial = new MeshBasicMaterial({
      color: this.standardColor
    });

    this.lineMaterial = new LineBasicMaterial({
      color: this.darkenedColor
    });

    this.polygonMaterial = new MeshBasicMaterial({
      color: this.standardColor,
      side: DoubleSide
    });
  }

  complete() {
    this.completed = true;
    this.editing = false;
    this.draw();
  }

  draw(temporaryPoint=null) {
    this.clear();

    if (this.category.currentCamera !== this.camera) {
      return;
    }

    this.drawPoints(temporaryPoint);
    this.drawLines(temporaryPoint);
    this.drawPolygons(temporaryPoint);
  }

  clear() {
    this.standardMeshes.forEach(mesh => {
      this.standardScene.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    });

    this.opacityMeshes.forEach(mesh => {
      this.opacityScene.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    });

    this.standardMeshes = [];
    this.opacityMeshes = [];
  }

  /** @private */
  drawPoints(temporaryPoint) {
    let positions = this.positions;

    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.position];
    }

    positions.forEach((position, index) => {
      const coordinate = this.coordinates[index];
      const mesh = new LabelPoint(this.pointGeometry,
        this.pointMaterial, coordinate);

      mesh.position.copy(position);

      this.standardScene.add(mesh);
      this.standardMeshes.push(mesh);
      setPointScale(this.viewer, mesh);
    });
  }

  /** @private */
  drawLines(temporaryPoint) {
    let positions = this.positions;
    if (positions.length === 0) {
      return;
    }

    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.position];
    }

    let corners = [...positions, positions[0]];
    this.lineGeometry.setFromPoints(corners);

    const line = new Line(this.lineGeometry, this.lineMaterial);
    this.standardMeshes.push(line);
    this.standardScene.add(line);
  }

  /** @private */
  drawPolygons(temporaryPoint) {
    let positions = this.positions;
    let directions = this.directions;

    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.position];
      directions = [...directions, temporaryPoint.direction];
    }

    if (positions.length < this.minCoordinates) {
      return;
    }

    // Virtual pixels are calculated as a planar camera, facing the
    // direction of the approximated plane created from the points
    // so polygons can be estimated using earcut
    const scale = 100;
    const plane = planeFromVectors(directions);
    const points = directions.map(x => {
      let target = new Vector3();
      plane.projectPoint(x, target);
      return target;
    });

    // Check for valid polygon
    let coordinates = points.map(x => [x.x, x.y]);
    let valid = checkValidPolygon(coordinates);
    if (!valid) {
      return;
    }

    // Calculate triangle mesh indices
    let pointsFlat = new Array(points.length * 2);
    points.forEach((pixel, index) => {
      pointsFlat[2*index] = pixel.x * scale;
      pointsFlat[2*index + 1] = pixel.y * scale;
    });

    let indices = earcut(pointsFlat, null, 2);

    // Deviation must be nearly zero to be valid
    let deviation = earcut.deviation(pointsFlat, null, 2, indices);
    if (deviation > 1E-6) {
      return;
    }

    let numTriangles = indices.length / 3;
    for (var i = 0; i < numTriangles; i++) {
      let geom = new Geometry();

      let triangle = new Triangle(
        points[indices[3 * i]],
        points[indices[3 * i + 1]],
        points[indices[3 * i + 2]]
      );

      let center = new Vector3();
      triangle.getMidpoint(center);
      let valid = pointInPolygon(center, points);
      if (!valid) {
        continue;
      }

      geom.vertices.push(
        positions[indices[3 * i]],
        positions[indices[3 * i + 1]],
        positions[indices[3 * i + 2]]
      );
      let face = new Face3(0, 1, 2);
      geom.faces.push(face);

      let mesh = new Mesh(geom, this.polygonMaterial);
      this.opacityMeshes.push(mesh);
      this.opacityScene.add(mesh);
    }
  }
}

class SceneCategory extends LabelCategory {
  public sceneLabels: SceneLabelTools;
  public labels: SceneLabel[] = [];

  constructor(sceneLabels, name) {
    super(name);
    this.sceneLabels = sceneLabels;
  }

  get color() : Color {
    let color = textToColor(this.name);
    return new Color(color);
  }

  get currentCamera() {
    return this.sceneLabels.currentCamera;
  }

  addNewLabel() {
    let label = new SceneLabel(this, this.currentCamera);
    this.labels.push(label);

    return label;
  };

  deleteUnfinishedLabel() {
    let numberOfLabels = this.labels.length;
    if (numberOfLabels === 0) {
      return;
    }

    if (!this.labels[numberOfLabels - 1].completed) {
      this.labels.pop();
    }
  }
}

export class SceneLabelTools {
  public parent: ImageLabelTools;
  public standardScene: Scene;
  public opacityScene: Scene;
  private setViewerTooltipText;
  public enabled = false;
  public opacity = 0.2;
  private meshDistance = 100;
  private minPixelDistance = 5;

  private temporaryPoint: Coordinate;
  public movingEndpoint = false;
  public draggable = false;
  private dragObject: LabelPoint = null;

  constructor(parent: ImageLabelTools, props) {
    const {setViewerTooltipText} = props;

    this.parent = parent;
    this.standardScene = new Scene();
    this.opacityScene = new Scene();
    this.setViewerTooltipText = setViewerTooltipText;
  }

  get categories() {
    return this.parent.categories
      .filter(x => x instanceof SceneCategory) as SceneCategory[];
  }

  get viewer() {
    return this.parent.viewer;
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get currentCamera() {
    let current = this.cameraList.current;
    if (this.viewer.orbitState || !current) {
      return null;
    }

    return current;
  }

  get drawing() {
    let label = this.activeLabel;
    if (!label || label.completed) {
      return false;
    }

    if (label.coordinates.length === 0) {
      return false;
    }

    return true;
  }

  get activeLabel() {
    let selected = null;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.selected) {
          selected = label;
        }
      });
    });

    return selected;
  }

  get moreTooltip() {
    return `
      Click to add more points (${this.activeLabel.numPoints})
      ${(this.activeLabel.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get startTooltip() {
    return "Click to start drawing polygon";
  }

  setState(state) {
    this.enabled = state;
    this.resetToolTipText();
  }

  getNewCategory(name) {
    return new SceneCategory(this, name);
  }

  getClickedDirection(event) {
    const raycaster = new RayCaster(this.viewer, event);
    const direction = raycaster.vectorFromEvent(event);
    return direction;
  }

  getMouseResults(event) {
    const direction = this.getClickedDirection(event);
    if (!direction) {
      return;
    }

    const pixel = this.currentCamera
      .directionToPixel(direction);

    const position = new Vector3()
      .copy(direction)
      .multiplyScalar(this.meshDistance)
      .add(this.currentCamera.position.viewer);

    return {pixel, direction, position};
  }

  mouseClick(event) {
    if (!this.currentCamera) {
      return;
    }

    let label = this.activeLabel;
    if (!label) {
      return;
    }

    let {pixel, direction, position} = this.getMouseResults(event);

    let duplicatePoint = false;
    let numCoordinates = label.coordinates.length;
    if (numCoordinates !== 0) {
      // Check for distance from previous point
      let previous = label.coordinates[numCoordinates -1];
      let dU = pixel.u - previous.pixel.u;
      let dV = pixel.v - previous.pixel.v;
      let distance = Math.sqrt(dU**2 + dV**2);

      if (distance < this.minPixelDistance) {
        duplicatePoint = true;
      }
    }

    if (duplicatePoint && (numCoordinates >= label.minCoordinates)) {
      // Finish label
      this.completeLabel(event, label);
      return;
    }

    // Add new point
    let coordinate = new Coordinate(pixel, direction, position);
    label.coordinates.push(coordinate);

    this.setToolTipText();
    this.temporaryPoint = null;
    this.drawAll();
  }

  completeLabel(event, label) {
    this.temporaryPoint = null;
    this.parent.completeLabel(label.id);
    this.checkForGrabbingPoint(event);
  }

  cancelLabel(event) {
    let label = this.activeLabel;
    if (!label) {
      return;
    }

    this.parent.deleteLabel(label.id);
    this.setToolTipText();
    this.checkForGrabbingPoint(event);
    this.drawAll();
  }

  checkForGrabbingPoint(event) {
    // Small timeout is needed for whatever reason
    let timeoutMilliseconds = 20;

    setTimeout(() => {
      this.onMouseMove(event);
      if (this.draggable) {
        this.setPointGrabTooltip();
      }
    }, timeoutMilliseconds);
  }

  checkPointSelected(event) {
    if (this.drawing) {
      return;
    }

    let targets = this.standardScene.children
      .filter(x => x instanceof LabelPoint);

    let draggable = false;
    let dragObject = null;

    if (targets.length > 0) {
      let raycaster = new RayCaster(this.viewer, event);
      let objects = raycaster.intersectObjects(targets);
      draggable = objects.length > 0;
      dragObject = draggable ? objects[0].object : null;
    }

    this.setDragState(draggable, dragObject);
  }

  updateDraggablePoint(event) {
    let coordinate = this.dragObject.coordinate;
    let {pixel, direction, position} = this.getMouseResults(event);
    coordinate.updateValues(pixel, direction, position);
    this.drawAll();
  }

  setDragState(grabbing, dragObject) {
    this.draggable = grabbing;
    this.dragObject = dragObject;
  }

  setToolTipText() {
    if (!this.activeLabel) {
      this.resetToolTipText();
      return;
    }

    let text = "";
    if (this.drawing) {
      text = this.moreTooltip;
    } else {
      if (this.currentCamera) {
        text = this.startTooltip;
      }
    }

    this.setViewerTooltipText(text);
  }

  resetToolTipText() {
    this.setViewerTooltipText("");
  }

  setPointGrabTooltip() {
    this.setViewerTooltipText("Click and hold to move vertex");
  }

  onMouseMove(event, mouse = null) {
    const mouseMoved = mouse ? mouse.movement : false;
    const mouseDown = mouse ? mouse.down : false;
    const isLeftClick = mouse ? mouse.button === 0 : false;

    this.temporaryPoint = null;
    this.movingEndpoint = false;

    if (!this.enabled) {
      return;
    }

    if (!mouseMoved && !mouseDown) {
      this.checkPointSelected(event);
    }

    if (mouse === null) {
      return;
    }

    if (this.drawing) {
      let {pixel, direction, position} = this.getMouseResults(event);
      this.temporaryPoint = new Coordinate(pixel, direction, position);
      this.drawAll();
    }

    if (this.draggable) {
      this.setPointGrabTooltip();
      if (mouseDown) {
        if (isLeftClick) {
          this.updateDraggablePoint(event);
        }
        this.movingEndpoint = true;
        return;
      }
    } else {
      this.setToolTipText();
    }
  }

  drawAll() {
    this.standardScene.clear();
    this.opacityScene.clear();

    if (this.viewer.orbitState) {
      return;
    }

    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.draw(this.temporaryPoint);
      });
    });
  }

  updateColor() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.updateMaterials();
      });
    });
  }

  update(changeDetector) {
    const update = changeDetector.changed;
    if (!update) {
      return;
    }

    if (changeDetector.cameraChanged || changeDetector.orbitChanged) {
      this.categories.forEach(category => {
        // Remove incomplete observation if camera changed
        category.deleteUnfinishedLabel();
        this.temporaryPoint = null;
      });

      this.drawAll();
      return;
    }

    this.standardScene.children.forEach(mesh => {
      if (mesh instanceof LabelPoint) {
        setPointScale(this.viewer, mesh);
      }
    });
  }
}

